import { supabase } from "@/integrations/supabase/client";

/**
 * Unified trailer-BoL → order sync core.
 *
 * Replaces the previous trio: `bol-trailer-sync.ts`, `trailer-bol-carry-forward.ts`,
 * `trailer-bol-fanout-to-orders.ts`. Every UI surface that (a) attaches a
 * trailer to an order, or (b) attaches/updates a BoL on a trailer MUST call
 * this helper (typically via `useTrailerBolSync`).
 *
 * Two modes:
 *   - "order-gains-trailer": user changed an order's trailer_id. Pull the
 *     trailer's latest check-in BoL forward onto this order.
 *   - "trailer-gains-bol":  a BoL was just added to (or carried onto) a
 *     trailer_event. Fan it out to every active order currently assigned to
 *     this trailer. Optional `carryForward` first copies the trailer's most
 *     recent BoL onto the freshly-created event.
 *
 * Best-effort semantics: never throws. All observable outcomes (success,
 * skip, error, forbidden) are written to `trailer_bol_fanout_log` by the
 * server (or client, for pre-invoke failures) so the July 2026-style silent
 * miss is diagnosable.
 */

export type TrailerBolSyncResult = {
  synced: number;
  skipped: number;
  errors: string[];
  orderIds: string[];
};

export type TrailerBolSyncArgs =
  | {
      direction: "order-gains-trailer";
      orderId: string;
      tenantSlug: string | null | undefined;
    }
  | {
      direction: "trailer-gains-bol";
      trailerHumanId: string | null | undefined;
      tenantId: string | null | undefined;
      tenantSlug: string | null | undefined;
      /**
       * If provided, first copies the trailer's most recent prior BoL
       * onto `newEventId` (via `trailer_event_documents`) before fanning
       * out. Used by yard/driver check-in flows that create a fresh
       * `checked_in` event without a captured BoL.
       */
      carryForward?: {
        trailerDbId: string;
        newEventId: string;
      };
    };

const EMPTY: TrailerBolSyncResult = { synced: 0, skipped: 0, errors: [], orderIds: [] };

async function logFanout(row: {
  tenantId: string | null | undefined;
  trailerId?: string | null;
  orderId?: string | null;
  eventId?: string | null;
  result: "forbidden" | "error" | "skipped";
  reason: string;
  detail?: unknown;
}): Promise<void> {
  if (!row.tenantId) return;
  try {
    await supabase.from("trailer_bol_fanout_log" as never).insert({
      tenant_id: row.tenantId,
      trailer_id: row.trailerId ?? null,
      order_id: row.orderId ?? null,
      event_id: row.eventId ?? null,
      result: row.result,
      reason: row.reason,
      detail: row.detail ?? null,
    } as never);
  } catch (err) {
    console.warn("trailer_bol_fanout_log insert failed:", err);
  }
}

/**
 * Copy the trailer's most recent prior BoL onto a freshly-created event so
 * the "current BoL" travels forward across audits / re-check-ins where no
 * new BoL was captured.
 */
async function carryForwardBol(params: {
  trailerDbId: string;
  newEventId: string;
  tenantId?: string | null;
}): Promise<void> {
  const { trailerDbId, newEventId, tenantId } = params;
  try {
    // Empty trailers don't have BoLs — never carry a prior BoL onto an
    // event that recorded the trailer as empty. Judged on the new event's
    // own load_status (point-in-time), not the trailer's current state.
    const { data: targetEvt, error: targetErr } = await supabase
      .from("trailer_events")
      .select("load_status")
      .eq("id", newEventId)
      .maybeSingle();
    if (targetErr) {
      console.warn("carryForwardBol target event lookup failed:", targetErr.message);
      return;
    }
    if ((targetEvt as { load_status?: string | null } | null)?.load_status === "empty") {
      return;
    }

    let q = supabase
      .from("trailer_events")
      .select(
        "id, created_at, trailer_event_documents!inner(file_path, file_name, document_type, tenant_id)",
      )
      .eq("trailer_id", trailerDbId)
      .eq("trailer_event_documents.document_type", "bol")
      .neq("id", newEventId)
      .order("created_at", { ascending: false })
      .limit(1);
    if (tenantId) q = q.eq("tenant_id", tenantId);

    const { data: evtRow, error } = await q.maybeSingle();
    if (error) {
      console.warn("carryForwardBol lookup failed:", error.message);
      return;
    }
    const docs = (evtRow as any)?.trailer_event_documents as
      | Array<{ file_path: string; file_name: string; tenant_id?: string | null }>
      | undefined;
    const doc = docs && docs.length ? docs[0] : null;
    if (!doc) return;

    const payload: any = {
      trailer_event_id: newEventId,
      file_path: doc.file_path,
      file_name: doc.file_name,
      document_type: "bol",
    };
    if (tenantId) payload.tenant_id = tenantId;
    else if (doc.tenant_id) payload.tenant_id = doc.tenant_id;

    const { error: insertErr } = await supabase
      .from("trailer_event_documents")
      .insert(payload);
    if (insertErr) {
      console.warn("carryForwardBol insert failed:", insertErr.message);
    }
  } catch (err) {
    console.warn("carryForwardBol threw:", err);
  }
}

async function invokeSyncForOrder(params: {
  orderId: string;
  tenantSlug: string;
  tenantIdForLog?: string | null;
  trailerIdForLog?: string | null;
}): Promise<{ synced: number; skipped: number; error?: string }> {
  const { orderId, tenantSlug, tenantIdForLog, trailerIdForLog } = params;
  try {
    const { data, error } = await supabase.functions.invoke(
      "sync-trailer-bol-to-order",
      { body: { order_id_uuid: orderId, tenant_slug: tenantSlug } },
    );
    if (error) {
      // Non-2xx from edge function: log so we can trace forbidden vs error.
      const isForbidden = /403|forbidden/i.test(error.message ?? "");
      await logFanout({
        tenantId: tenantIdForLog,
        trailerId: trailerIdForLog,
        orderId,
        result: isForbidden ? "forbidden" : "error",
        reason: error.message ?? "invoke error",
      });
      return { synced: 0, skipped: 0, error: error.message };
    }
    if (data?.skipped) return { synced: 0, skipped: 1 };
    return {
      synced: typeof data?.synced === "number" ? data.synced : 0,
      skipped: typeof data?.skipped === "number" ? data.skipped : 0,
    };
  } catch (err: any) {
    await logFanout({
      tenantId: tenantIdForLog,
      trailerId: trailerIdForLog,
      orderId,
      result: "error",
      reason: err?.message ?? "threw",
    });
    return { synced: 0, skipped: 0, error: err?.message ?? "threw" };
  }
}

export async function syncTrailerBol(
  args: TrailerBolSyncArgs,
): Promise<TrailerBolSyncResult> {
  if (!args.tenantSlug) return EMPTY;

  // ── Mode A: order-gains-trailer ─────────────────────────────────────────
  if (args.direction === "order-gains-trailer") {
    const res = await invokeSyncForOrder({
      orderId: args.orderId,
      tenantSlug: args.tenantSlug,
    });
    return {
      synced: res.synced,
      skipped: res.skipped,
      errors: res.error ? [res.error] : [],
      orderIds: res.synced > 0 || res.skipped > 0 ? [args.orderId] : [],
    };
  }

  // ── Mode B: trailer-gains-bol ───────────────────────────────────────────
  const { trailerHumanId, tenantId, tenantSlug, carryForward } = args;
  if (!trailerHumanId || !tenantId) return EMPTY;

  if (carryForward) {
    await carryForwardBol({
      trailerDbId: carryForward.trailerDbId,
      newEventId: carryForward.newEventId,
      tenantId,
    });
  }

  try {
    // Active = anything not terminal. Deliberately no leg-state filter — the
    // BoL belongs to the order regardless of which leg is currently moving.
    // NOTE: values MUST match the `order_status` enum exactly. Adding a
    // non-existent value here makes PostgREST reject the whole IN list with
    // an enum cast error, silently killing every trailer-BoL fanout. See
    // `trailer-bol-sync.test.ts` for the guardrail.
    const closedStatuses = "(delivered,canceled)";

    const { data: orderMatches, error: orderErr } = await supabase
      .from("orders")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("trailer_id", trailerHumanId)
      .not("status", "in", closedStatuses);
    if (orderErr) {
      console.warn("syncTrailerBol order lookup failed:", orderErr.message);
      await logFanout({
        tenantId,
        trailerId: trailerHumanId,
        result: "error",
        reason: `order lookup failed: ${orderErr.message}`,
      });
      return { synced: 0, skipped: 0, errors: [`order lookup failed: ${orderErr.message}`], orderIds: [] };
    }

    const orderIds = new Set<string>((orderMatches ?? []).map((o: { id: string }) => o.id));

    const { data: legMatches, error: legErr } = await supabase
      .from("legs" as any)
      .select("order_id, orders!inner(id, tenant_id, status)")
      .eq("trailer_id", trailerHumanId)
      .eq("tenant_id", tenantId);
    if (legErr) {
      console.warn("syncTrailerBol leg lookup failed:", legErr.message);
    } else {
      for (const row of (legMatches ?? []) as any[]) {
        const ord = Array.isArray(row.orders) ? row.orders[0] : row.orders;
        if (!ord?.id) continue;
        if (["delivered", "canceled"].includes(ord.status)) continue;
        orderIds.add(ord.id);
      }
    }

    if (orderIds.size === 0) {
      await logFanout({
        tenantId,
        trailerId: trailerHumanId,
        eventId: carryForward?.newEventId ?? null,
        result: "skipped",
        reason: "no_active_orders",
      });
      return EMPTY;
    }

    const results = await Promise.all(
      Array.from(orderIds).map((id) =>
        invokeSyncForOrder({
          orderId: id,
          tenantSlug,
          tenantIdForLog: tenantId,
          trailerIdForLog: trailerHumanId,
        }),
      ),
    );
    return {
      synced: results.reduce((a, r) => a + r.synced, 0),
      skipped: results.reduce((a, r) => a + r.skipped, 0),
      errors: results.map((r) => r.error).filter((e): e is string => !!e),
      orderIds: Array.from(orderIds),
    };
  } catch (err: any) {
    console.warn("syncTrailerBol threw:", err);
    await logFanout({
      tenantId,
      trailerId: trailerHumanId,
      result: "error",
      reason: err?.message ?? "threw",
    });
    return EMPTY;
  }
}